import React, { useEffect, useState } from "react"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core"

import { enqueueSnackbar } from "notistack"
import { useTranslation } from "react-i18next"

import { ChatResponse } from "../api/chat-response"
import { useChatMutations } from "../hooks/use-chat-mutations"

type Props = {
  open: boolean
  chat: ChatResponse
  onClose: () => void
  onUpdated?: (chatId: string) => void
}

const RenameChatDialog = ({ open, chat, onClose, onUpdated }: Props) => {
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")
  const { updateChat } = useChatMutations()

  const [name, setName] = useState(chat.name ?? "")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setName(chat.name ?? "")
    }
  }, [open, chat])

  const canSave = name.trim().length > 0 && name.trim() !== chat.name

  const handleSave = async () => {
    if (!canSave) return

    try {
      setLoading(true)

      const result = await updateChat(chat.id, {
        name: name.trim(),
      })
      if (!result) return
      enqueueSnackbar(t("renameChatDialog.successMessage"), {
        variant: "success",
      })

      onUpdated?.(chat.id)
      onClose()
    } catch {
      enqueueSnackbar(t("renameChatDialog.errorMessage"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("renameChatDialog.title")}</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="normal"
          label={t("renameChatDialog.nameLabel")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          {common("cancel")}
        </Button>

        <Button color="primary" variant="contained" disabled={!canSave || loading} onClick={handleSave}>
          {loading ? <CircularProgress size={20} /> : common("save")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RenameChatDialog
